import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchVendorArtworks } from './api'

const SEARCH_DEBOUNCE_MS = 300

// The shop's reusable designs, one page at a time, with a debounced search.
export function useShopDesigns({
  enabled,
  vendorToken,
  builderSessionId,
  builderDraftPublicId,
  debounceMs = SEARCH_DEBOUNCE_MS,
}) {
  const [query, setQuery] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [artworks, setArtworks] = useState([])
  const [nextPage, setNextPage] = useState(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  // '' means every category; otherwise the public id of the chosen one.
  const [category, setCategoryState] = useState('')
  const [categories, setCategories] = useState([])
  // Set when the chosen category vanished and the list fell back to every design.
  const [categoryNotice, setCategoryNotice] = useState(null)
  // Whether the unfiltered catalog runs past one page; search is noise for a handful of designs.
  const [catalogSpansPages, setCatalogSpansPages] = useState(false)
  // Bumped by every new first-page load so answers to superseded requests are dropped.
  const requestRef = useRef(0)
  // The state flag lags a render behind, so two calls in one tick would both fetch.
  const loadingMoreRef = useRef(false)

  // Only the customer clears the notice, so it survives the reload the fallback itself triggers.
  const setCategory = useCallback(publicId => {
    setCategoryNotice(null)
    setCategoryState(publicId)
  }, [])
  const changeQuery = useCallback(value => {
    setCategoryNotice(null)
    setQuery(value)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setActiveQuery(query.trim()), debounceMs)
    return () => clearTimeout(timer)
  }, [query, debounceMs])

  useEffect(() => {
    const request = ++requestRef.current
    loadingMoreRef.current = false
    setLoadingMore(false)

    if (!enabled) {
      setArtworks([])
      setNextPage(null)
      setTotal(0)
      setError(null)
      setLoading(false)
      setCategories([])
      setCategoryState('')
      setCategoryNotice(null)
      return
    }

    setLoading(true)
    setError(null)
    fetchVendorArtworks(vendorToken, builderSessionId, builderDraftPublicId, { page: 1, q: activeQuery, category })
      .then(result => {
        if (request !== requestRef.current) return
        const list = result.categories ?? []
        setArtworks(result.artworks)
        setNextPage(result.nextPage)
        setTotal(result.total)
        setCategories(list)
        // Only the unfiltered catalog says whether search is worth offering.
        if (!activeQuery && !category) setCatalogSpansPages(result.nextPage != null)
        // The vendor hid or emptied the chosen category since the list was fetched.
        if (category && !list.some(entry => entry.publicId === category)) {
          setCategoryState('')
          setCategoryNotice('That category is no longer available. Showing all designs.')
        }
      })
      .catch(loadError => {
        if (request !== requestRef.current) return
        setArtworks([])
        setNextPage(null)
        setTotal(0)
        setError(loadError.message || 'Could not load shop designs')
      })
      .finally(() => {
        if (request === requestRef.current) setLoading(false)
      })

    // Supersede this load on unmount or when its inputs change.
    return () => { requestRef.current += 1 }
  }, [enabled, vendorToken, builderSessionId, builderDraftPublicId, activeQuery, category])

  const loadMore = useCallback(async () => {
    if (!enabled || nextPage == null || loading || loadingMoreRef.current) return

    const request = requestRef.current
    loadingMoreRef.current = true
    setLoadingMore(true)
    setError(null)
    try {
      const result = await fetchVendorArtworks(
        vendorToken,
        builderSessionId,
        builderDraftPublicId,
        { page: nextPage, q: activeQuery, category }
      )
      if (request !== requestRef.current) return

      // A design the vendor added in between shifts the offset, so a page can repeat one.
      setArtworks(current => {
        const known = new Set(current.map(artwork => String(artwork.publicId)))
        return [...current, ...result.artworks.filter(artwork => !known.has(String(artwork.publicId)))]
      })
      setNextPage(result.nextPage)
      setTotal(result.total)
    } catch (loadError) {
      if (request === requestRef.current) setError(loadError.message || 'Could not load shop designs')
    } finally {
      if (request === requestRef.current) {
        loadingMoreRef.current = false
        setLoadingMore(false)
      }
    }
  }, [activeQuery, builderDraftPublicId, builderSessionId, category, enabled, loading, nextPage, vendorToken])

  return {
    artworks,
    loading,
    loadingMore,
    error,
    query,
    setQuery: changeQuery,
    activeQuery,
    total,
    hasMore: nextPage != null,
    loadMore,
    searchable: catalogSpansPages || activeQuery !== '',
    category,
    setCategory,
    categories,
    categoryNotice,
  }
}
